import {
  Box,
  Button,
  Image,
  Skeleton,
  Table,
  Tbody,
  Td,
  Thead,
  Text,
  Tr,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { ReceivableResponseTapePreviewGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  AssignmentHighlightBox,
  AssignmentIncompleteCurrentMasterLoanTapeSetAlert,
} from './AssignmentTemplate';
import mainLogo from 'packages/common-frontend/images/Logo-Horizontal-Normal.svg';
import { useGetCreditFacilitiesQuery } from 'packages/credit-facility/requests/requests.gql.generated';
import { useGetCompletedReceivableResponseTapePreviewsByMasterLoanTapeIdsQuery } from 'packages/loanProduct/requests/requests.gql.generated';
import { AreMasterLoanTapesAssignedCheckContainer } from 'packages/loanProduct/ui/AreMasterLoanTapesAssignedCheckContainer';
import { AssignmentLoadingSkeleton } from 'packages/loanProduct/ui/AssignmentSkeletonLoadingTables';
import { useReceivableResponseTapePreviewContext } from 'packages/loanProduct/ui/ReceivableResponseTapePreviewContext';
import { parseCompleteCurrentMasterLoanTapeSetForLoanProductIds } from 'packages/loanProduct/ui/utils';
import { AssignmentTemplate } from 'packages/loanProduct/ui/v2/AssignmentTemplate';
import {
  useGetOrganizationLoanProductsQuery,
  useGetOrganizationsByIdsQuery,
} from 'packages/organization/requests/organization.gql.generated';

const FinleyLogo = () => {
  const [logoLoaded, setLogoLoaded] = useState(false);
  return (
    <Image
      src={mainLogo}
      alt="Finley"
      width="70px"
      display={logoLoaded ? 'block' : 'none'}
      height="70%"
      onLoad={() => {
        setLogoLoaded(true);
      }}
    />
  );
};

export const AssignmentSavedVersionsTableRow = ({
  receivableResponseTapePreview,
  borrowerName,
}: {
  receivableResponseTapePreview: ReceivableResponseTapePreviewGQLType;
  borrowerName: string;
}) => {
  const {
    isInitialCondition,
    fileVersion,
    reportingDate,
    previewIdentifier,
    completedAt,
  } = receivableResponseTapePreview;

  const { dispatch } = useReceivableResponseTapePreviewContext();

  const previewSelectCallback = () => {
    dispatch({
      type: 'setPreview',
      payload: {
        previewIdentifier,
        reportingDate,
        fileVersion,
      },
    });
  };

  return (
    <Tr>
      <Td>
        <Text>{isInitialCondition ? <FinleyLogo /> : borrowerName}</Text>
      </Td>
      <Td>
        <Button
          variant="link"
          onClick={previewSelectCallback}
          color="blue.600"
          textDecoration="underline"
        >
          {completedAt
            ? commonDate.epochToLocaleDateString(completedAt)
            : 'N/A'}
        </Button>
        <Text mt="5px" fontSize="12px">
          Reporting date: {reportingDate}
        </Text>
        <Text fontSize="12px">File version: {fileVersion}</Text>
      </Td>
    </Tr>
  );
};

export const AssignmentSavedVersionsTable = ({
  organizationId,
  masterLoanTapeIds,
}: {
  organizationId: string;
  masterLoanTapeIds: string[];
}) => {
  const {
    data: organizationsData,
    loading: organizationsLoading,
  } = useGetOrganizationsByIdsQuery({
    variables: { organizationIds: [organizationId] },
  });

  const {
    data: previewsData,
    loading: previewsLoading,
  } = useGetCompletedReceivableResponseTapePreviewsByMasterLoanTapeIdsQuery({
    variables: { organizationId, masterLoanTapeIds },
    fetchPolicy: 'no-cache', // can update to network-only if we ever want the cached version at all
  });

  if (
    !organizationsData ||
    !organizationsData.organizationsByIds ||
    organizationsLoading ||
    !previewsData ||
    !previewsData.completedReceivableResponseTapePreviewsByMasterLoanTapeIds ||
    previewsLoading
  ) {
    return <Skeleton data-testid="loading-history" height="200px" />;
  }

  const borrowerName = organizationsData.organizationsByIds[0].name;
  const {
    completedReceivableResponseTapePreviewsByMasterLoanTapeIds: previews,
  } = previewsData;

  // multiple previews may have the same preview identifier hash
  // ie. a record can exist for multiple credit facilities with the same preview identifier
  const uniqueOrderedPreviews = _.orderBy(
    _.uniqBy(previews, 'previewIdentifier'),
    'completedAt',
    'desc',
  );

  return (
    <Table>
      <Thead>
        <Tr>
          <Td>Owner</Td>
          <Td>Last Saved</Td>
        </Tr>
      </Thead>
      <Tbody>
        {_.map(uniqueOrderedPreviews, (preview) => {
          return (
            <AssignmentSavedVersionsTableRow
              receivableResponseTapePreview={preview}
              key={preview.previewIdentifier}
              borrowerName={borrowerName}
            />
          );
        })}
      </Tbody>
    </Table>
  );
};

// add SavedVersionsContainer here that has the basic help text

export const AssignmentSavedVersionsContainer = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const {
    data: organizationLoanProductsData,
    loading: organizationLoanProductsLoading,
  } = useGetOrganizationLoanProductsQuery({
    variables: {
      organizationId,
    },
  });

  const {
    data: creditFacilitiesForBorrowerData,
    loading: creditFacilitiesForBorrowerLoading,
  } = useGetCreditFacilitiesQuery({ variables: { organizationId } });

  if (
    !organizationLoanProductsData ||
    !organizationLoanProductsData.organization ||
    organizationLoanProductsLoading ||
    !creditFacilitiesForBorrowerData ||
    !creditFacilitiesForBorrowerData.creditFacilities ||
    creditFacilitiesForBorrowerLoading
  ) {
    return (
      <AssignmentLoadingSkeleton
        dataTestId="loading-assignment-history-container"
        selectedTab="history"
      />
    );
  }

  const loanProductsForOrganization =
    organizationLoanProductsData.organization.loanProducts;

  const completeCurrentMasterLoanTapeSetForLoanProductIds = parseCompleteCurrentMasterLoanTapeSetForLoanProductIds(
    loanProductsForOrganization,
  );

  if (!completeCurrentMasterLoanTapeSetForLoanProductIds) {
    return (
      <AssignmentIncompleteCurrentMasterLoanTapeSetAlert selectedTab="history" />
    );
  }

  const {
    fileVersion,
    reportingDate,
    masterLoanTapeIds,
  } = completeCurrentMasterLoanTapeSetForLoanProductIds;

  const assignmentSavedVersionsHelpText =
    'To restore a saved version, click on the timestamp indicating the last time the assignment was saved.';

  return (
    <Box height="100%">
      <AreMasterLoanTapesAssignedCheckContainer
        masterLoanTapeIds={masterLoanTapeIds}
        fileVersion={fileVersion}
        reportingDate={reportingDate}
        selectedTab="history"
      >
        <AssignmentTemplate selectedTab="history">
          <Text pt="32px" mb="32px">
            {assignmentSavedVersionsHelpText}
          </Text>
          <Box w="50%">
            <AssignmentHighlightBox>
              <AssignmentSavedVersionsTable
                organizationId={organizationId}
                masterLoanTapeIds={masterLoanTapeIds}
              />
            </AssignmentHighlightBox>
          </Box>
        </AssignmentTemplate>
      </AreMasterLoanTapesAssignedCheckContainer>
    </Box>
  );
};
